/**
 * Re-Edit persis Delphi (gelombang 7) — daftar transaksi + registry token otorisasi ubah.
 *
 * Delphi TFormMGTrans.ActionEditExecute (Forms/Template/MGTransForm.pas:1137-1212): mengubah nota yang SUDAH DICETAK
 * (CountPrint > 0, butuh hak Re-Edit), yang tanggalnya BUKAN HARI INI (butuh "Ijinkan Edit Transaksi bukan hari ini" di
 * Global Information), atau tanpa hak Edit sama sekali → Log In user lain yang berhak. Pasangan BE:
 * mgmx_be/src/services/reEdit.service.js (kunci `jenis` HARUS sama dengan TRANSAKSI di sana).
 *
 * Alur web: form Edit dibuka → hooks/useGerbangEdit memanggil GET /auth/cek-edit/<jenis>/<id>; bila perlu, dialog
 * Log In (OtorisasiModal) → token otorisasi disimpan di registry ini → interceptor axios (lib/axios.ts) menempelkan
 * header `X-Otorisasi-Edit` ke PUT ubah nota itu → BE memeriksa ulang saat menyimpan.
 */

/** Jenis transaksi Re-Edit → path API (relatif baseURL `/api`) dan akhiran PUT yang dibawa token. */
export const REEDIT_API = {
    'po-beli': { base: '/transaksi/po-beli' },
    pembelian: { base: '/transaksi/pembelian' },
    'retur-beli': { base: '/transaksi/retur-beli' },
    'biaya-lain': { base: '/transaksi/biaya-lain' },
    'bayar-hut': { base: '/transaksi/bayar-hut' },
    'um-beli': { base: '/transaksi/um-beli' },
    'koreksi-hut-cn': { base: '/transaksi/koreksi-hut-cn' },
    'koreksi-hut-dn': { base: '/transaksi/koreksi-hut-dn' },
    jual: { base: '/transaksi/jual' },
    'so-jual': { base: '/transaksi/so-jual' },
    'retur-jual': { base: '/transaksi/retur-jual' },
    'bayar-piut': { base: '/transaksi/bayar-piut' },
    'um-jual': { base: '/transaksi/um-jual' },
    'koreksi-piut-cn': { base: '/transaksi/koreksi-piut-cn' },
    'koreksi-piut-dn': { base: '/transaksi/koreksi-piut-dn' },
    'pakai-dp': { base: '/transaksi/pakai-dp' },
    'pencairan-kartu': { base: '/transaksi/pencairan-kartu' },
    'kas-masuk': { base: '/transaksi/kas-masuk' },
    'kas-keluar': { base: '/transaksi/kas-keluar' },
    'mutasi-kas-masuk': { base: '/transaksi/mutasi-kas-masuk' },
    'mutasi-kas-keluar': { base: '/transaksi/mutasi-kas-keluar' },
    'giro-cair-masuk': { base: '/transaksi/giro-cair-masuk' },
    'giro-cair-keluar': { base: '/transaksi/giro-cair-keluar' },
    'giro-tolak-masuk': { base: '/transaksi/giro-tolak-masuk' },
    'giro-tolak-keluar': { base: '/transaksi/giro-tolak-keluar' },
    'giro-ganti-masuk': { base: '/transaksi/giro-ganti-masuk' },
    'giro-ganti-keluar': { base: '/transaksi/giro-ganti-keluar' },
    jurnal: { base: '/transaksi/jurnal' },
    'jenis-jurnal': { base: '/master/jenis-jurnal' },
    'pembelian-aset': { base: '/transaksi/beli-aset' },
    'penjualan-aset': { base: '/transaksi/jual-aset' },
    'penyesuaian-stock': { base: '/transaksi/penyesuaian-brg' },
    'opname-cetak': { base: '/transaksi/opname-brg' },
    'opname-input': { base: '/transaksi/opname-brg', akhiran: ['/input-result'] },
    // Simpan Edit Transfer Kirim bisa diikuti PUT /send (form) — token yang sama ikut dibawa
    'transfer-kirim': { base: '/transaksi/transfer-brg', akhiran: ['', '/send'] },
} as const satisfies Record<string, { base: string; akhiran?: readonly string[] }>;

export type JenisReEdit = keyof typeof REEDIT_API;

/** Header token otorisasi ubah (BE reEdit.service HEADER). */
export const HEADER_OTORISASI_EDIT = 'X-Otorisasi-Edit';
/** Flag otorisasi BE (otorisasi.service FLAG_REEDIT). */
export const FLAG_REEDIT = 'ReEdit';
/** Format(wmUserNoAuth, ['edit']) — Units/MGMessage.pas:57 */
export const PESAN_TIDAK_BERHAK_EDIT = 'Maaf, anda tidak punya otoritas untuk edit .';
/** Kode 403 BE saat simpan tanpa hak / token otorisasi sah. */
export const KODE_PERLU_OTORISASI_EDIT = 'PERLU_OTORISASI_EDIT';

/** Path PUT yang dibawa token untuk nota `id` jenis `jenis`. */
export function pathUbah(jenis: JenisReEdit, id: string | number): string[] {
    const api: { base: string; akhiran?: readonly string[] } = REEDIT_API[jenis];
    const akhiran = api.akhiran ?? [''];
    return akhiran.map((a) => `${api.base}/${encodeURIComponent(String(id))}${a}`);
}

/** path PUT → token (sesi halaman; hilang saat muat ulang — Delphi juga meminta Log In setiap kali Edit). */
const registry = new Map<string, string>();

export function simpanTokenUbah(jenis: JenisReEdit, id: string | number, token: string): void {
    for (const p of pathUbah(jenis, id)) registry.set(p, token);
}

export function hapusTokenUbah(jenis: JenisReEdit, id: string | number): void {
    for (const p of pathUbah(jenis, id)) registry.delete(p);
}

/** Normalisasi URL request axios → path relatif `/api` tanpa query / garis miring akhir. */
function normalPath(url: string): string {
    let p = String(url || '');
    const q = p.search(/[?#]/);
    if (q >= 0) p = p.slice(0, q);
    p = p.replace(/^https?:\/\/[^/]+/i, '').replace(/^\/?api(?=\/)/, '');
    if (!p.startsWith('/')) p = `/${p}`;
    return p.replace(/\/+$/, '') || '/';
}

/** Token otorisasi ubah untuk request (hanya PUT ke path nota yang terdaftar), atau null. */
export function tokenUbahUntuk(method: string | undefined, url: string | undefined): string | null {
    if (String(method || '').toLowerCase() !== 'put' || !url || registry.size === 0) return null;
    return registry.get(normalPath(url)) ?? null;
}
